import { Message } from "@cnx/common";
import { ActionIcon, Box, Divider, Flex, Modal, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronLeft, IconDownload, IconX } from "@tabler/icons-react";
import theme from "common/theme";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "stores/authStore";
import DateFormat from "types/DateFormat";
import { DocumentWithId } from "types/document";
import { SwipeableImageViewer } from "./SwipeableImageViewer";

interface ImageMessageViewProps {
  message?: Message;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  documents: DocumentWithId[];
  initialIndex?: number;
}

export const ImageMessageView: React.FC<ImageMessageViewProps> = ({
  message,
  showModal,
  setShowModal,
  documents,
  initialIndex,
}) => {
  const isDesktop =
    useMediaQuery(`(min-width: ${theme.breakpoints?.lg})`) ?? false;
  const currentUserId = useAuthStore(state => state.currentUserId);
  const { t } = useTranslation("discussions");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    documents.length === 1 ? 0 : null
  );

  useEffect(() => {
    if (!showModal) {
      setSelectedIndex(
        isDesktop ? initialIndex ?? 0 : documents.length === 1 ? 0 : null
      );
      return;
    }
    setSelectedIndex(
      isDesktop
        ? initialIndex ?? 0
        : initialIndex ?? (documents.length === 1 ? 0 : null)
    );
  }, [showModal, documents.length, initialIndex, isDesktop]);

  const selected = selectedIndex !== null ? documents[selectedIndex] : null;

  const handleDownload = () => {
    if (!selected) return;
    const url = URL.createObjectURL(selected.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selected.id}.${
      selected.blob.type.split("/").at(1) ?? "jpg"
    }`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBack = () => {
    if (!isDesktop && selectedIndex !== null && documents.length > 1) {
      setSelectedIndex(null);
    } else {
      setShowModal(false);
    }
  };

  const subTitle =
    selectedIndex !== null
      ? dayjs(message?.createdTime ?? "").format(DateFormat.DEFAULT)
      : `${documents.length} ${(documents.length > 1
          ? t("images")
          : t("image")
        ).toLowerCase()} • ${dayjs(message?.createdTime ?? "").format(
          DateFormat.ONLY_DATE
        )}`;

  return (
    <Modal
      opened={showModal}
      onClose={() => setShowModal(false)}
      padding={0}
      radius={0}
      zIndex={20000}
      fullScreen
      styles={{
        content: { backgroundColor: "black" },
        header: { display: "none" },
        body: {
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          overscrollBehavior: "contain",
          backgroundColor: "black",
        },
      }}
    >
      {isDesktop ? (
        <>
          <Flex justify="flex-end" gap={20} p={20} style={{ flexShrink: 0 }}>
            {selected && (
              <ActionIcon
                variant="transparent"
                size={40}
                onClick={handleDownload}
              >
                <IconDownload size={36} color="white" stroke={2} />
              </ActionIcon>
            )}
            <ActionIcon
              variant="transparent"
              size={40}
              onClick={() => setShowModal(false)}
            >
              <IconX size={36} color="white" stroke={1} />
            </ActionIcon>
          </Flex>
          <Divider color="var(--color-gray-medium)" />
        </>
      ) : (
        <>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
              height: 60,
              backgroundColor: "var(--color-gray-dark)",
            }}
          >
            <Flex align="center">
              <ActionIcon variant="transparent" size={40} onClick={handleBack}>
                <IconChevronLeft size={24} color="var(--color-gray-medium)" />
              </ActionIcon>
              <Flex direction="column" ml={15}>
                <Text c="white" size="sm" fw={600}>
                  {message?.createdBy !== currentUserId
                    ? message?.senderName
                    : t("you")}
                </Text>
                <Text c="white" size="xs" fw={400}>
                  {subTitle}
                </Text>
              </Flex>
            </Flex>

            {selected && (
              <Flex align="center" gap={4} mr={8}>
                <ActionIcon
                  variant="transparent"
                  size={40}
                  onClick={handleDownload}
                >
                  <IconDownload size={24} color="var(--color-white)" />
                </ActionIcon>
              </Flex>
            )}
          </Box>
        </>
      )}
      <Box style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <SwipeableImageViewer
          documents={documents}
          selectedIndex={selectedIndex}
          onSelectedIndexChange={setSelectedIndex}
          isDesktop={isDesktop}
          messageText={message?.message}
        />
      </Box>
    </Modal>
  );
};
